import BuildingProfile from './BuildingProfile';
// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LoginPage from './LoginPage';
// import SignupPage from './SignupPage';
import RoleBasedRoute from './RoleBasedRoute';
import ProtectedRoute from './ProtectedRoute';
import { analytics } from './firebase'; // import the analytics export
import Dashboard from './Dashboard';
import FieldHome from './FieldHome';
import FieldBuildingRun from './FieldBuildingRun';
import AddUnitForm from './AddUnitForm';
import MaintenanceSchedule from './MaintenanceSchedule';
import MaintenanceSeasonalPlanning from './MaintenanceSeasonalPlanning';
import MaintenanceContractRevenue from './MaintenanceContractRevenue';
import MaintenanceHub from './MaintenanceHub';
import MaintenanceHome from './MaintenanceHome';
import MaintenanceOverview from './MaintenanceOverview';
import MaintenanceAnalytics from './MaintenanceAnalytics';
import MaintenanceCheckIns from './MaintenanceCheckIns';
import MaintenanceReviews from './MaintenanceReviews';
import PublicLeaderboard from './PublicLeaderboard';
import MaintenanceFinancialOverview from './MaintenanceFinancialOverview';
import MaintenanceFinancialMetric from './MaintenanceFinancialMetric';
import MaintenancePnlCalendar from './MaintenancePnlCalendar';
import MaintenanceBuildingHeatmap from './MaintenanceBuildingHeatmap';
import MaintenanceQuotaTracker from './MaintenanceQuotaTracker';
import MaintenanceMechManagement from './MaintenanceMechManagement';
import MaintenanceMechanicalCalendar from './MaintenanceMechanicalCalendar';
import MaintenanceJobberLinks from './MaintenanceJobberLinks';
import MaintenanceDocuments from './MaintenanceDocuments';
import MaintenanceCrewFleet from './MaintenanceCrewFleet';
import MaintenanceDivisionMargins from './MaintenanceDivisionMargins';
import MaintenanceAffiliatePayouts from './MaintenanceAffiliatePayouts';
import ManageAccounts from './ManageAccounts';
import BuildingIndex from './BuildingIndex';
import MaintenanceProtectedRoute, { CategoryGate } from './MaintenanceProtectedRoute';
import { CAT_ADMIN, CAT_SERVICE, CAT_INSUITE, CAT_FINANCE, CAT_DISCOVERY } from './maintenanceAccess';
import MechanicalRoom from './MechanicalRoom';
import MechanicalLayout from './MechanicalLayout';
import Equipment from './Equipment';
import DryerVents from './DryerVents';
import StackCleaning from './StackCleaning';
import WaterSavings from './WaterSavings';
import Reports from './Reports';
import RegionalDashboard from './RegionalDashboard';
import CorporateDashboard from './CorporateDashboard';
import CorporateProposals from './CorporateProposals';
import BMIDashboard from './BMIDashboard';
import Projects from './Projects';
import ValveAudit from './ValveAudit';
import WorkOrder from './WorkOrder';
import MechScheduleHome from './MechScheduleHome';
import MechBuildingDetail from './MechBuildingDetail';
import MechVisitDetail from './MechVisitDetail';
import MechVisitChecklist from './MechVisitChecklist';
import MechIssueForm from './MechIssueForm';
import PreviewBanner from './PreviewBanner';

function useAnalytics() {
  const location = useLocation();
  useEffect(() => {
    import("firebase/analytics").then(({ logEvent }) => {
      logEvent(analytics, 'page_view', {
        page_path: location.pathname,
        page_title: document.title,
      });
    });
  }, [location]);
}

function useClarityIdentify() {
  useEffect(() => {
    try {
      const raw = localStorage.getItem('managerUser');
      if (!raw || typeof window.clarity !== 'function') return;
      const user = JSON.parse(raw);
      const name = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username;
      window.clarity('identify', user.username, null, null, name);
      if (user.role) window.clarity('set', 'role', user.role);
      if (user.management_company) window.clarity('set', 'management_company', user.management_company);
      if (user.buildingId) window.clarity('set', 'building_id', String(user.buildingId));
      window.clarity('set', 'page_type', 'manager_dashboard');
    } catch (_) {}
  }, []);
}

const localProfilePreview = process.env.NODE_ENV === 'development' && process.env.REACT_APP_PROFILE_PREVIEW === 'true';
function LocalProfileHome({accounts = false}) {
  let user; try { user = JSON.parse(localStorage.getItem('managerUser') || '{}'); } catch { user = {}; }
  const staff = String(user.role || '').startsWith('maintenance');
  if (accounts && !staff) return <Navigate to="/profile" replace />;
  return staff ? <ManageAccounts /> : <BuildingProfile />;
}
function LocalPreviewBanner() {
  const location = useLocation();
  if (!localProfilePreview || location.pathname === '/login') return null;
  const signOut = () => { localStorage.removeItem('managerToken'); localStorage.removeItem('managerUser'); sessionStorage.removeItem('redirectAfterLogin'); window.location.href = '/login'; };
  return <div className="bp-preview">Local test database · <a href="/profile">Building profile</a> · <a href="/profile-preview/accounts">Manage Accounts</a> · <button type="button" onClick={signOut}>Sign out</button></div>;
}
function App() {
  useAnalytics();
  useClarityIdentify();

  return (
    <>
      {/* No-op outside of preview tabs; see previewSession.js + PreviewBanner.js */}
      <PreviewBanner />
      <LocalPreviewBanner />
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      {/* Public technician leaderboard — no auth, shareable.
          Backed by /api/reviews/internal/leaderboard. */}
      <Route path="/leaderboard" element={<PublicLeaderboard />} />
      {/* <Route path="/signup" element={<SignupPage />} /> */}
      
      {/* Field routes (separate auth system) */}
      <Route path="/schedule" element={<FieldHome />} />
      <Route path="/schedule/buildings/:buildingId" element={<FieldBuildingRun />} />
      <Route path="/schedule/buildings/:buildingId/add-unit" element={<AddUnitForm />} />
      {/* Same catch-all guard as /mech-schedule below: keeps unmatched
          sub-paths out of "/" so field techs never land on /login. */}
      <Route path="/schedule/buildings" element={<Navigate to="/schedule" replace />} />
      <Route path="/schedule/*" element={<Navigate to="/schedule" replace />} />
      
      {/* Mechanical room tech schedule (public, no auth) */}
      <Route path="/mech-schedule" element={<MechScheduleHome />} />
      <Route path="/mech-schedule/buildings/:buildingId" element={<MechBuildingDetail />} />
      <Route path="/mech-schedule/buildings/:buildingId/visits/:visitId" element={<MechVisitDetail />} />
      <Route path="/mech-schedule/buildings/:buildingId/visits/:visitId/checklist" element={<MechVisitChecklist />} />
      <Route path="/mech-schedule/buildings/:buildingId/issue" element={<MechIssueForm />} />
      {/* Without these, /mech-schedule/buildings and any other unmatched
          sub-path fall through to the global catch-all, which routes via "/"
          and bounces techs to /login even though this flow is public. */}
      <Route path="/mech-schedule/buildings" element={<Navigate to="/mech-schedule" replace />} />
      <Route path="/mech-schedule/*" element={<Navigate to="/mech-schedule" replace />} />

      {/* Maintenance Schedule (public for now) */}
      <Route path="/maintenance-schedule" element={<MaintenanceSchedule />} />

      {/* Maintenance Hub — single internal home for the maintenance team.
          Wrapped in MaintenanceProtectedRoute: requires a logged-in user
          whose role is exactly "maintenance". Anyone else is sent to /login
          (if logged out) or shown an explicit access-denied panel. */}
      <Route path="/maintenance" element={<MaintenanceProtectedRoute><MaintenanceHub /></MaintenanceProtectedRoute>}>
        <Route index element={<MaintenanceOverview />} />
        <Route path="home" element={<Navigate to="/maintenance" replace />} />
        <Route path="in-suite" element={<MaintenanceHome />} />
        {/* `key` forces a remount when flipping between /schedule and
            /calendar so the underlying MaintenanceSchedule picks up the
            new `initialTab` (useState only honours initial values on mount).
            `embedded` strips the back arrow + internal tab toggle and drops
            pills on the calendar — the hub provides all of that chrome. */}
        <Route path="schedule" element={<MaintenanceSchedule key="hub-schedule" initialTab="list" embedded />} />
        <Route path="seasonal-planning" element={<MaintenanceSeasonalPlanning />} />
        <Route path="calendar" element={<MaintenanceSchedule key="hub-calendar" initialTab="calendar" embedded />} />
        <Route path="analytics" element={<MaintenanceAnalytics />} />
        {/* Contract Revenue sits under the in-suite heading but is
            L3-only — it exposes portfolio-wide revenue. */}
        <Route
          path="contract-revenue"
          element={<CategoryGate category={CAT_SERVICE}><MaintenanceContractRevenue /></CategoryGate>}
        />
        <Route path="check-ins" element={<MaintenanceCheckIns />} />
        {/* Admin category (maintenance-L2 and L3, plus legacy "maintenance"). */}
        <Route
          path="manage-accounts"
          element={<CategoryGate category={CAT_ADMIN}><ManageAccounts /></CategoryGate>}
        />
        <Route path="documents" element={<CategoryGate category={CAT_ADMIN}><MaintenanceDocuments /></CategoryGate>} />
        <Route path="crew-fleet" element={<CategoryGate category={CAT_ADMIN}><MaintenanceCrewFleet /></CategoryGate>} />
        <Route
          path="reviews"
          element={<CategoryGate category={CAT_ADMIN}><MaintenanceReviews /></CategoryGate>}
        />
        <Route
          path="building-index"
          element={<CategoryGate category={CAT_DISCOVERY}><BuildingIndex /></CategoryGate>}
        />
        <Route path="money-overview" element={<CategoryGate category={CAT_SERVICE}><MaintenanceFinancialOverview /></CategoryGate>} />
        <Route path="money-overview/:metric" element={<CategoryGate category={CAT_SERVICE}><MaintenanceFinancialMetric /></CategoryGate>} />
        <Route path="revenue-breakdown" element={<Navigate to="/maintenance/money-overview" replace />} />
        {/* Division Margins — revenue/costs/profit per service division.
            L3-only (CAT_SERVICE): company-wide financials. */}
        <Route
          path="division-margins"
          element={<CategoryGate category={CAT_SERVICE}><MaintenanceDivisionMargins /></CategoryGate>}
        />
        {/* Affiliate Payouts — commission rates and money leaving the company.
            CAT_FINANCE is L3-only, deliberately narrower than CAT_ADMIN. */}
        <Route
          path="affiliate-payouts"
          element={<CategoryGate category={CAT_FINANCE}><MaintenanceAffiliatePayouts /></CategoryGate>}
        />
        {/* Service category (Jobber-backed, maintenance-L3 + legacy). Start
            with PnL Calendar — more tabs (AR aging, stuck quotes, tech
            leaderboard) will land here. */}
        <Route
          path="pnl-calendar"
          element={<CategoryGate category={CAT_SERVICE}><MaintenancePnlCalendar /></CategoryGate>}
        />
        <Route
          path="building-heatmap"
          element={<CategoryGate category={CAT_SERVICE}><MaintenanceBuildingHeatmap /></CategoryGate>}
        />
        {/* Visible to every maintenance tier (L1+): coordinators use it
            to check their own quotas. */}
        <Route
          path="quota-tracker"
          element={<CategoryGate category={CAT_INSUITE}><MaintenanceQuotaTracker /></CategoryGate>}
        />
        {/* Back-office frame of the tech /mech-schedule flow — same
            visibility as the Service Tracker (every tier, L1+). */}
        <Route
          path="mech-management"
          element={<CategoryGate category={CAT_INSUITE}><MaintenanceMechManagement /></CategoryGate>}
        />
        <Route
          path="mech-calendar"
          element={<CategoryGate category={CAT_INSUITE}><MaintenanceMechanicalCalendar /></CategoryGate>}
        />
        {/* Which Jobber master job each contract points at. Office-only
            (CAT_ADMIN), matching the other Jobber-linking surfaces. */}
        <Route
          path="mech-jobber-links"
          element={<CategoryGate category={CAT_ADMIN}><MaintenanceJobberLinks /></CategoryGate>}
        />
        <Route path="*" element={null} />
      </Route>
      
      {/* Work Order form (public) */}
      <Route path="/workorder" element={<WorkOrder />} />
      
      {/* Protected routes - require authentication */}
      <Route path="/" element={localProfilePreview ? <ProtectedRoute><LocalProfileHome /></ProtectedRoute> : <RoleBasedRoute />} />
      <Route path="/mechanical-room" element={<ProtectedRoute><MechanicalLayout /></ProtectedRoute>}>
        <Route index element={<MechanicalRoom />} />
        <Route path="equipment/:equipmentId" element={<Equipment />} />
      </Route>
      {process.env.NODE_ENV === 'development' && process.env.REACT_APP_PROFILE_PREVIEW === 'true' && <>
        <Route path="/profile-preview" element={<Navigate to="/profile" replace />} />
        <Route path="/profile-preview/accounts" element={<ProtectedRoute><LocalProfileHome accounts /></ProtectedRoute>} />
      </>}
      <Route path="/profile" element={<ProtectedRoute><BuildingProfile /></ProtectedRoute>} />
      <Route path="/dryer-vents" element={<ProtectedRoute><DryerVents /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
      <Route path="/valve-audit" element={<ProtectedRoute><ValveAudit /></ProtectedRoute>} />
      <Route path="/stack-cleaning" element={<ProtectedRoute><StackCleaning /></ProtectedRoute>} />
      <Route path="/water-savings" element={<ProtectedRoute><WaterSavings /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/regional-dashboard" element={<ProtectedRoute><RegionalDashboard /></ProtectedRoute>} />
      <Route path="/corporate-dashboard" element={<ProtectedRoute><CorporateDashboard /></ProtectedRoute>} />
      <Route path="/corporate/proposals" element={<ProtectedRoute><CorporateProposals /></ProtectedRoute>} />
      <Route path="/bmi" element={<ProtectedRoute><BMIDashboard /></ProtectedRoute>} />
      
      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default function Root() {
  return (
    <Router>
      <App />
    </Router>
  );
}
